import { useState } from 'react';
import { RefreshCw, AlertCircle, CheckCircle, Trash2, RotateCcw, Zap } from 'lucide-react';
import pwaAuthFix from '../../utils/pwaAuthFix';
import usePWA from '../../hooks/usePWA';

const PWATroubleshoot = ({ onClose }) => {
  const [isFixing, setIsFixing] = useState(false);
  const [fixResults, setFixResults] = useState([]);
  const { forceUpdate, updateAvailable } = usePWA();

  const addResult = (message, success = true) => {
    setFixResults(prev => [...prev, { message, success, timestamp: Date.now() }]);
  };

  const clearCache = async () => {
    setIsFixing(true);
    addResult('Starting cache cleanup...');
    
    try {
      // Clear cache storage
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        for (const name of cacheNames) {
          await caches.delete(name);
          addResult(`✅ Cleared cache: ${name}`);
        }
      }
      
      await pwaAuthFix.clearAuthCache();
      addResult('✅ Auth cache cleared');
      
      // Clear family data localStorage
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('family-data-')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      addResult(`✅ Cleared ${keysToRemove.length} family data items`);
      
      // Clear session storage
      sessionStorage.clear();
      addResult('✅ Session storage cleared');
      
      addResult('✅ Cache cleanup complete');
    } catch (error) {
      addResult(`❌ Cache cleanup failed: ${error.message}`, false);
    }
    
    setIsFixing(false);
  };

  const forceRefresh = () => {
    addResult('🔄 Forcing app refresh...');
    setTimeout(() => {
      pwaAuthFix.forceRefresh();
    }, 1000);
  };

  const unregisterServiceWorker = async () => {
    setIsFixing(true);
    addResult('Unregistering service worker...');
    
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
          addResult('✅ Service worker unregistered');
        }
      }
      addResult('✅ All service workers removed');
    } catch (error) {
      addResult(`❌ Failed to unregister: ${error.message}`, false);
    }
    
    setIsFixing(false);
  };

  const handleForceUpdate = async () => {
    setIsFixing(true);
    addResult('🚀 Starting complete PWA reset...');
    
    try {
      addResult('This will clear all caches and restart the app');
      await forceUpdate();
    } catch (error) {
      addResult(`❌ Force update failed: ${error.message}`, false);
      setIsFixing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-96 overflow-hidden">
        <div className="p-4 border-b border-stone-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">PWA Troubleshoot</h3>
            <button
              onClick={onClose}
              className="text-stone-400 hover:text-slate-600"
            >
              ✕
            </button>
          </div>
          <p className="text-sm text-slate-600 mt-1">
            Fix common PWA authentication and loading issues
          </p>
        </div>

        <div className="p-4 space-y-3">
          {updateAvailable && (
            <div className="bg-azure-50 border border-azure-200 rounded-lg p-3 mb-3">
              <div className="flex items-center space-x-2 text-azure-700">
                <AlertCircle size={16} />
                <span className="font-medium">Update Available</span>
              </div>
              <p className="text-sm text-azure-600 mt-1">
                A new version is ready. Try the simple refresh first.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <button
              onClick={forceRefresh}
              disabled={isFixing}
              className="w-full flex items-center justify-center space-x-2 bg-emerald-500 text-white py-2 px-4 rounded-lg hover:bg-emerald-600 disabled:opacity-50"
            >
              <RefreshCw size={16} />
              <span>Refresh App</span>
            </button>

            <button
              onClick={clearCache}
              disabled={isFixing}
              className="w-full flex items-center justify-center space-x-2 bg-azure-500 text-white py-2 px-4 rounded-lg hover:bg-azure-600 disabled:opacity-50"
            >
              <Trash2 size={16} />
              <span>Clear All Caches</span>
            </button>

            <button
              onClick={unregisterServiceWorker}
              disabled={isFixing}
              className="w-full flex items-center justify-center space-x-2 bg-yellow-500 text-white py-2 px-4 rounded-lg hover:bg-yellow-600 disabled:opacity-50"
            >
              <RotateCcw size={16} />
              <span>Reset Service Worker</span>
            </button>

            <div className="border-t pt-2 mt-3">
              <button
                onClick={handleForceUpdate}
                disabled={isFixing}
                className="w-full flex items-center justify-center space-x-2 bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                <Zap size={16} />
                <span>Complete Reset (Last Resort)</span>
              </button>
              <p className="text-xs text-stone-500 mt-1 text-center">
                Only use if app is completely stuck
              </p>
            </div>
          </div>

          {fixResults.length > 0 && (
            <div className="bg-stone-50 rounded-lg p-3 max-h-32 overflow-y-auto">
              <h4 className="text-sm font-medium text-slate-700 mb-2">Results:</h4>
              <div className="space-y-1">
                {fixResults.map((result, index) => (
                  <div key={index} className="flex items-start space-x-2 text-xs">
                    {result.success ? (
                      <CheckCircle size={12} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    ) : (
                      <AlertCircle size={12} className="text-red-500 mt-0.5 flex-shrink-0" />
                    )}
                    <span className={result.success ? 'text-emerald-700' : 'text-red-700'}>
                      {result.message}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 bg-stone-50 text-xs text-slate-600">
          <p><strong>When to use:</strong></p>
          <ul className="mt-1 space-y-0.5 ml-2">
            <li>• App won't load after sign-in</li>
            <li>• Stuck on loading screen</li>
            <li>• Authentication issues</li>
            <li>• App feels slow or cached</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PWATroubleshoot;